import {
  SystemTurn,
  UserTurn,
  ResponseActionProps,
  CardMessageProps,
  CarouselMessageProps,
  FeedbackProps,
} from "@tomis-tech/chat-ui";

import { ChatWidgetTurn, WelcomeMessage } from "@/types";

/**
 * Convert API turn data to UI turn format
 */
export function convertApiTurnsToUITurns(
  apiTurns: ChatWidgetTurn[],
  avatarImage: string,
  onActionClick: (action: {
    type: string;
    name: string;
    key?: string;
    faq_id?: number;
    welcome_button_id?: number;
  }) => void,
  buildFeedbackProps?: (turn: ChatWidgetTurn) => FeedbackProps | undefined,
  onLinkClick?: (href: string, turnId?: string) => void,
): Array<SystemTurn | UserTurn> {
  return apiTurns.map((turn) => {
    const timestamp = new Date(turn.timestamp).getTime();

    // Business-side turns (bot "system" and live "agent" operator replies) render
    // on the left; only the customer's own "user" turns render on the right.
    const isFromBusiness = turn.type === "system" || turn.type === "agent";

    if (isFromBusiness) {
      const messages = [];

      if (turn.text) {
        messages.push({ type: "text", text: turn.text });
      }

      if (turn.card) {
        // Wrap card action onClick handlers to track URL clicks
        const wrappedActions = turn.card.actions?.map((action) => {
          if (action.url) {
            return {
              ...action,
              onClick: () => onLinkClick?.(action.url!, turn.id),
            };
          }
          return action;
        });

        const card: CardMessageProps = {
          type: "card",
          title: turn.card.title,
          description: turn.card.description || "",
          image: turn.card.image,
          actions: wrappedActions,
        };
        messages.push(card);
      }

      if (turn.carousel) {
        // Wrap carousel card action onClick handlers to track URL clicks
        const wrappedCards = turn.carousel.map((carouselCard) => {
          const wrappedActions = carouselCard.actions?.map((action) => {
            if (action.url) {
              return {
                ...action,
                onClick: () => onLinkClick?.(action.url!, turn.id),
              };
            }
            return action;
          });

          return {
            ...carouselCard,
            actions: wrappedActions,
          };
        });

        const carousel: CarouselMessageProps = {
          type: "carousel",
          cards: wrappedCards.sort(
            (a, b) => (b.actions?.length || 0) - (a.actions?.length || 0),
          ),
        };
        messages.push(carousel);
      }

      return {
        id: turn.id,
        type: "system",
        source: turn.type === "agent" ? "agent" : "bot",
        suppressOriginSwitch: turn.custom?.suppress_origin_switch === true,
        props: {
          avatar: avatarImage,
          id: turn.id,
          messages: messages,
          timestamp,
          senderName: turn.type === "agent" ? turn.agent_name : undefined,
          // Feedback thumbs apply to bot answers only, not operator replies.
          feedback:
            turn.type === "system" && buildFeedbackProps
              ? buildFeedbackProps(turn)
              : undefined,
          actions:
            turn.buttons?.map((b) => {
              if (b.type === "form") {
                return {
                  name: b.name,
                  type: "form",
                  variant: "action",
                  size: "xl",
                  width: "100%",
                  onClick: () =>
                    onActionClick({
                      type: "form",
                      name: b.name,
                      key: b.key,
                    }),
                } as ResponseActionProps;
              }
              // default to existing quick reply behavior
              return {
                name: b.name,
                type: b.type,
                onClick: () =>
                  onActionClick({
                    type: b.type,
                    name: b.name,
                    ...(b.type === "quick_reply"
                      ? {
                          ...(b.faq_id ? { faq_id: b.faq_id } : {}),
                          ...(b.welcome_button_id !== undefined
                            ? { welcome_button_id: b.welcome_button_id }
                            : {}),
                        }
                      : {}),
                  }),
              } as ResponseActionProps;
            }) || [],
          useAnimation: false, // disable animation for historical turns
          onLinkClick: onLinkClick
            ? (href: string) => onLinkClick(href, turn.id)
            : undefined,
        },
      } as SystemTurn;
    } else {
      return {
        id: turn.id,
        type: "user",
        props: {
          message: turn.text,
          timestamp,
        },
      } as UserTurn;
    }
  });
}

/**
 * Convert welcome messages to UI system turns
 */
export function convertWelcomeMessagesToTurns(
  welcomeMessages: WelcomeMessage[],
  avatarImage: string,
  onActionClick: (action: {
    type: string;
    name: string;
    key?: string;
    faq_id?: number;
    welcome_button_id?: number;
  }) => void,
  onLinkClick?: (href: string, turnId?: string) => void,
): Array<SystemTurn> {
  if (!welcomeMessages?.length) {
    return [];
  }

  return welcomeMessages.map((welcomeMsg) => {
    return {
      id: welcomeMsg.id as string,
      type: "system",
      source: "bot",
      props: {
        avatar: avatarImage,
        id: welcomeMsg.id as string,
        messages: welcomeMsg.messages,
        timestamp: new Date().getTime(),
        useAnimation: true,
        onLinkClick,
        actions:
          welcomeMsg.actions?.map((a) => {
            if (a.type === "form") {
              return {
                name: a.name,
                type: "form",
                variant: "action",
                size: "xl",
                width: "100%",
                onClick: () =>
                  onActionClick({
                    type: "form",
                    name: a.name,
                    key: a.key,
                  }),
              } as ResponseActionProps;
            }
            return {
              name: a.name,
              type: a.type,
              onClick: () =>
                onActionClick({
                  type: a.type,
                  name: a.name,
                  ...(a.type === "quick_reply"
                    ? {
                        ...(a.faq_id ? { faq_id: a.faq_id } : {}),
                        ...(a.welcome_button_id !== undefined
                          ? { welcome_button_id: a.welcome_button_id }
                          : {}),
                      }
                    : {}),
                }),
            } as ResponseActionProps;
          }) || [],
      },
    };
  });
}

/**
 * Create a mock ChatWidgetTurn for testing or development
 */
export function createMockChatWidgetTurn(
  id: string,
  type: "system" | "user",
  text: string,
  options?: {
    timestamp?: string;
    buttons?: Array<{ type: "quick_reply"; name: string }>;
  },
): ChatWidgetTurn {
  return {
    id,
    type,
    text,
    timestamp: options?.timestamp || new Date().toISOString(),
    ...(options?.buttons && { buttons: options.buttons }),
  };
}

/**
 * Create mock conversation history for testing
 */
export function createMockConversation(
  turns: Array<{
    type: "system" | "user";
    text: string;
    buttons?: Array<{ type: "quick_reply"; name: string }>;
  }>,
): ChatWidgetTurn[] {
  return turns.map((turn, index) =>
    createMockChatWidgetTurn(`mock-${index + 1}`, turn.type, turn.text, {
      buttons: turn.buttons,
    }),
  );
}
